import { Box, Loader } from '@wix/design-system';
import React, { FC, useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Attachment, AttachmentType } from '../../../../../types';
import { attachesSlice } from '../../../../common/reducers/attaches';
import s from '../styles.scss';
import { attachmentsPreviewDataHooks } from './AttachmentsPreview.dataHooks';

type AttachmentPreviewProps = Readonly<{
  attachment: Attachment;
}>;

const POLLING_INTERVAL = 2000;
const MAX_RETRIES = 20;

export const AttachmentPreviewImage: FC<AttachmentPreviewProps> = ({ attachment }) => {
  const dispatch = useDispatch();
  const [reloadPolling, setReloadPolling] = useState(false);
  const retriesCountRef = useRef(0);

  const onImageError = useCallback(() => {
    if (retriesCountRef.current > MAX_RETRIES) {
      dispatch(
        attachesSlice.actions.updateAttachment({
          ...attachment,
          previewFailed: true,
        }),
      );
      return;
    }

    // according to Media Platform team:
    // Unlike the pictures, video post processing is asynchronous. During post processing we are extracting video posters and transcoding the video
    // in case if the poster is not ready, trying to poll HEAD requests. Usually video posters are ready for usage in some seconds
    // Discussion https://wix.slack.com/archives/C0DAZUTKP/p1725259126592119
    retriesCountRef.current++;
    // remove <img> from DOM and put it back in 2s(POLLING_INTERVAL) to reload image
    setReloadPolling(true);
    setTimeout(() => setReloadPolling(false), POLLING_INTERVAL);
  }, [dispatch, attachment]);

  const onImageLoad = useCallback(
    () =>
      dispatch(
        attachesSlice.actions.updateAttachment({
          ...attachment,
          previewLoaded: true,
        }),
      ),
    [dispatch, attachment],
  );

  return (
    <>
      {!attachment.previewLoaded && !attachment.previewFailed && (
        <Box width="100%" height="100%" align="center" verticalAlign="middle">
          <Loader size="tiny" />
        </Box>
      )}
      {!reloadPolling && (
        <img
          src={attachment.type === AttachmentType.Video ? attachment.iconUrl : attachment.url}
          className={s.image}
          alt={attachment.name}
          onError={onImageError}
          onLoad={onImageLoad}
          data-hook={attachmentsPreviewDataHooks.image()}
        />
      )}
    </>
  );
};
